/**
 * OrdersSection.tsx — the patient portal's "My orders" section (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (patient
 * portal basis, 2026-07-22).
 *
 * WHY: the focused history view — the patient's OWN orders and their billing
 * history (held vs captured vs refunded phases). Money is shown in full: these
 * are the patient's own bills, not a staff money-redaction surface (integer
 * PENCE; 0 → "price set at launch"; dates true-UTC). A fresh account with no
 * orders gets the warm empty state (a 404 dashboard read is that same empty
 * state, never an error — matching Dashboard.tsx). Every fetch is retryable
 * (TA-06).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { getDashboard } from '../api/orders';
import type { DashboardData } from '../api/types';
import { displayTreatmentName } from '../lib/patientStatusCopy';
import { ButtonArrow, buttonClass, buttonStyle, Card, ErrorState, Help, PageFrame, Title } from '../ui/kit';
import { OrdersHistoryCard } from '../ui/OrdersHistoryCard';
import { SectionTitle } from '../ui/sections';
import { BillsCard } from './BillsCard';
import { SkeletonCard } from '../ui/Skeleton';

/** The empty dashboard shape a fresh account is treated as (matches Dashboard.tsx). */
const EMPTY_DASHBOARD: DashboardData = { currentPlan: null, orders: [], bills: [] };

export function OrdersSection() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setState('loading');
    getDashboard()
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return EMPTY_DASHBOARD;
        throw err;
      })
      .then((dashboard) => {
        setData(dashboard);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    return (
      <PageFrame bare>
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          <SkeletonCard lines={3} label="Loading your orders…" />
          <SkeletonCard lines={3} />
        </div>
      </PageFrame>
    );
  }

  if (state === 'error' || !data) {
    return (
      <PageFrame bare>
        <ErrorState message="Sorry — we could not load your orders." onRetry={load} />
      </PageFrame>
    );
  }

  const isEmpty = data.orders.length === 0 && data.bills.length === 0;

  return (
    <PageFrame bare>
      <Title as="h2">My orders</Title>
      {isEmpty ? (
        <EmptyOrders />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          <OrdersHistoryCard orders={data.orders} />
          <BillsCard
            bills={data.bills}
            orderNameById={new Map(data.orders.map((o) => [o.orderCaseId, displayTreatmentName(o.medicationName, o.productKey)] as [string, string]))}
          />
        </div>
      )}
    </PageFrame>
  );
}


function EmptyOrders() {
  return (
    <Card style={{ textAlign: 'center', padding: 'var(--gps-s-6) var(--gps-s-4)' }}>
      <SectionTitle>No orders yet</SectionTitle>
      <Help style={{ maxWidth: '24rem', margin: '0 auto var(--gps-s-4)' }}>
        Your orders and payments will appear here once you&rsquo;ve completed your check.
      </Help>
      <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
        <Link to="/weightloss" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Start your 2-minute check
          <ButtonArrow />
        </Link>
      </div>
    </Card>
  );
}
